import React, { useEffect } from "react";
import { SchedulerStyled } from "./styled";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import moment from "moment";
import { ROUTE_PATH } from "utils/constants/routePath";
import {
  RENDER_BOOKING_STATUS_COLOR,
  RENDER_BOOKING_STATUS_LABEL,
} from "utils/functions/musicHall";
import { Cards, Icons, Inputs } from "components";

// คำอธิบายสีใต้ filter — สีและชื่อมาจาก helper ตัวเดียวกับป้าย Booking Status หน้า list
// (หน้า Table กับหน้า list เป็นแค่มุมมองต่างกันของข้อมูลชุดเดียว ต้องตรงกัน)
// status ที่ใช้สีเดียวกันจะถูกรวมเป็นช่องเดียว
const LEGEND_ITEMS = ["1", "2", "3", "4", "5", "6", "7", "8"].reduce(
  (result, status) => {
    const color = RENDER_BOOKING_STATUS_COLOR(status).bgColor;
    const label = RENDER_BOOKING_STATUS_LABEL(status);
    const group = result.find((e) => e.color === color);
    if (group) {
      group.labels.push(label);
    } else {
      result.push({ color, labels: [label] });
    }
    return result;
  },
  []
);

const Scheduler = ({
  columns,
  customerData,
  dateFilterValue,
  filterValue,
  holidayData,
  isFullscreen,
  permission,
  timeSlotData,
  selectedRoomId,
  onClickClosed,
  onChangeFilter,
  onChangeRoom,
  onDateFilter,
  onSubmit,
  onToggleFullscreen,
}) => {
  const schema = yup.object().shape({
    // test: yup.string().required("New password is required"),
  });

  const {
    handleSubmit,
    // formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (timeSlotData) {
      let timeSlotFilter = timeSlotData.filter(
        (e) =>
          moment(e.date).format("YYYY-MM-DD") ===
            moment(dateFilterValue).format("YYYY-MM-DD") &&
          e.reason_closed === "2"
      );
      if (timeSlotFilter && timeSlotFilter.length > 0) {
        let params = {};
        timeSlotFilter.forEach((e) => {
          params[
            `${e.start_time.replace(".", "_")}-${e.end_time.replace(".", "_")}`
          ] = true;
        });
        reset({ ...params });
      }
    }
  }, [timeSlotData]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useNavigate();

  // Helper function to check if two time ranges overlap
  const isTimeOverlap = (start1, end1, start2, end2) => {
    const convertTime = (timeStr) => {
      const [hours, minutes] = timeStr.split(".").map(Number);
      return hours * 60 + (minutes || 0);
    };

    const s1 = convertTime(start1);
    const e1 = convertTime(end1);
    const s2 = convertTime(start2);
    const e2 = convertTime(end2);

    // Check if ranges overlap: start1 < end2 AND start2 < end1
    return s1 < e2 && s2 < e1;
  };

  // event_type === "1" uses setup/cleanup window instead of event window
  const getBookingStart = (f) =>
    f.event_type === "1" ? f.setup_cleanup_start_time : f.event_start_time;
  const getBookingEnd = (f) =>
    f.event_type === "1" ? f.setup_cleanup_end_time : f.event_end_time;

  // DAY FILTER
  // =======================================
  // =======================================
  const ROW_HEIGHT = 60;

  const _renderCutomer = (e) => {
    if (holidayData.includes(moment(dateFilterValue).format("YYYY-MM-DD"))) {
      let customerList = [];
      for (let i = 0; i < columns.length; i++) {
        customerList.push(<Cards.CalendarDayMusicHallItem isHoliday={true} />);
      }
      return customerList;
    }

    let findTimeSlot = timeSlotData.filter(
      (f) =>
        moment(f.date).format("YYYY-MM-DD") ===
          moment(dateFilterValue).format("YYYY-MM-DD") &&
        isTimeOverlap(
          f.start_time,
          f.end_time,
          e.value.startTime,
          e.value.endTime
        )
    );

    let customerList = [];
    for (let i = 0; i < columns.length; i++) {
      let findTimeSlotRoom = findTimeSlot.find(
        (s) => s.room_ref_id === columns[i]._id && s.reason_closed === "2"
      );
      if (findTimeSlotRoom) {
        customerList.push(
          <Cards.CalendarDayMusicHallItem
            key={i}
            isHoliday={true}
            onClick={() => {
              if (permission.is_create) {
                onClickClosed(columns[i], e.value, false);
              }
            }}
          />
        );
      } else {
        customerList.push(
          <div key={i} style={{ flex: 1 }} className="cell">
            <Cards.CalendarDayMusicHallItem
              onClick={() => {
                if (permission.is_create) {
                  onClickClosed(columns[i], e.value, true);
                }
              }}
            />
          </div>
        );
      }
    }
    return customerList;
  };

  const _renderBookingsOverlay = () => {
    if (holidayData.includes(moment(dateFilterValue).format("YYYY-MM-DD"))) {
      return null;
    }
    const currentDate = moment(dateFilterValue).format("YYYY-MM-DD");
    return (
      <div className="bookings_overlay">
        {columns.map((col) => {
          const bookings = customerData.filter(
            (f) =>
              f.room_ref_id === col._id &&
              moment(f.booking_date).format("YYYY-MM-DD") === currentDate
          );
          return (
            <div key={col._id} className="booking_column">
              {bookings.map((booking) => {
                const start = getBookingStart(booking);
                const end = getBookingEnd(booking);
                if (!start || !end) return null;
                const startIdx = TIME_DATA.findIndex((slot) =>
                  isTimeOverlap(
                    start,
                    end,
                    slot.value.startTime,
                    slot.value.endTime
                  )
                );
                if (startIdx === -1) return null;
                let endIdx = startIdx;
                for (let i = TIME_DATA.length - 1; i >= startIdx; i--) {
                  if (
                    isTimeOverlap(
                      start,
                      end,
                      TIME_DATA[i].value.startTime,
                      TIME_DATA[i].value.endTime
                    )
                  ) {
                    endIdx = i;
                    break;
                  }
                }
                return (
                  <div
                    key={booking._id}
                    className="booking_card"
                    style={{
                      top: startIdx * ROW_HEIGHT,
                      height: (endIdx - startIdx + 1) * ROW_HEIGHT,
                    }}
                    onClick={() =>
                      navigate(
                        `${ROUTE_PATH.MUSIC_HALL_BOOKING}/detail/${booking._id}`
                      )
                    }
                  >
                    <Cards.CalendarDayMusicHallItem
                      bookerName={booking?.booker_first_name}
                      eventName={booking?.event_name}
                      status={booking?.status}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // WEEK FILTER
  // =======================================
  // =======================================
  const _renderDay = () => {
    const startOfWeek = moment(dateFilterValue, "YYYY-MM-DD").startOf("week");
    let days = [];
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="table_header">
          <div className="header_item">
            <div>{startOfWeek.format("ddd")}</div>
            <div>{startOfWeek.format("D")}</div>
          </div>
        </div>
      );
      startOfWeek.add(1, "day");
    }
    return days;
  };

  const _renderCalendar = () => {
    let days = [];
    TIME_DATA.forEach(() => {
      const startOfWeek = moment(dateFilterValue, "YYYY-MM-DD").startOf("week");
      for (let i = 0; i < 7; i++) {
        const isDayHoliday = holidayData.includes(
          startOfWeek.format("YYYY-MM-DD")
        );
        days.push(
          <div className="cell">
            <Cards.CalendarWeekMusicHallItem isHoliday={isDayHoliday} />
          </div>
        );
        startOfWeek.add(1, "day");
      }
    });

    return days;
  };

  const _renderWeekBookingsOverlay = () => {
    return (
      <div className="bookings_overlay">
        {[...Array(7)].map((_, dayIdx) => {
          const currentDay = moment(dateFilterValue, "YYYY-MM-DD")
            .startOf("week")
            .add(dayIdx, "day");
          const currentDateStr = currentDay.format("YYYY-MM-DD");
          const isDayHoliday = holidayData.includes(currentDateStr);
          return (
            <div key={dayIdx} className="booking_column">
              {!isDayHoliday &&
                customerData
                  .filter(
                    (f) =>
                      moment(f.booking_date).format("YYYY-MM-DD") ===
                        currentDateStr && f.status !== "1"
                  )
                  .map((booking) => {
                    const start = getBookingStart(booking);
                    const end = getBookingEnd(booking);
                    if (!start || !end) return null;
                    const startIdx = TIME_DATA.findIndex((slot) =>
                      isTimeOverlap(
                        start,
                        end,
                        slot.value.startTime,
                        slot.value.endTime
                      )
                    );
                    if (startIdx === -1) return null;
                    let endIdx = startIdx;
                    for (let i = TIME_DATA.length - 1; i >= startIdx; i--) {
                      if (
                        isTimeOverlap(
                          start,
                          end,
                          TIME_DATA[i].value.startTime,
                          TIME_DATA[i].value.endTime
                        )
                      ) {
                        endIdx = i;
                        break;
                      }
                    }
                    return (
                      <div
                        key={booking._id}
                        className="booking_card"
                        style={{
                          top: startIdx * ROW_HEIGHT,
                          height: (endIdx - startIdx + 1) * ROW_HEIGHT,
                        }}
                        onClick={() =>
                          navigate(
                            `${ROUTE_PATH.MUSIC_HALL_BOOKING}/detail/${booking._id}`
                          )
                        }
                      >
                        <Cards.CalendarDayMusicHallItem
                          bookerName={booking?.booker_first_name}
                          eventName={booking?.event_name}
                          status={booking?.status}
                        />
                      </div>
                    );
                  })}
            </div>
          );
        })}
      </div>
    );
  };

  const isHoliday = holidayData.includes(
    moment(dateFilterValue).format("YYYY-MM-DD")
  );

  return (
    <SchedulerStyled $filterValue={filterValue} $isFullscreen={isFullscreen}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="header">
          <div className="scheduler_left_col">
            <div className="btn_day_week">
              <div
                className={`btn ${filterValue === "day" ? "active" : ""}`}
                onClick={() => onChangeFilter("day")}
              >
                Day
              </div>
              <div
                className={`btn ${filterValue === "week" ? "active" : ""}`}
                onClick={() => onChangeFilter("week")}
              >
                Week
              </div>
            </div>
            {filterValue === "week" && (
              <div className="filter_room">
                <Inputs.Dropdown
                  value={selectedRoomId}
                  options={columns.map((e) => ({
                    label: e.name_th,
                    value: e._id,
                  }))}
                  onChange={onChangeRoom}
                />
              </div>
            )}
          </div>
          <div className="year_right_sec">
            <div className="year_selector">
              <button
                type="button"
                className="btn_next_prev"
                onClick={() => onDateFilter("prev")}
              >
                <Icons.ArrowPrev />
              </button>
              <span>
                {filterValue === "day"
                  ? moment(dateFilterValue).format("DD MMMM YYYY")
                  : `${moment(dateFilterValue)
                      .startOf("week")
                      .format("DD MMM YYYY")} - ${moment(dateFilterValue)
                      .endOf("week")
                      .format("DD MMM YYYY")}`}
              </span>
              <button
                type="button"
                className="btn_next_prev"
                onClick={() => onDateFilter("next")}
              >
                <Icons.ArrowNext />
              </button>
            </div>
            <Inputs.DateTime
              type="datetime-local"
              value={new Date(dateFilterValue)}
              onChange={(e) => {
                onDateFilter("select", e);
              }}
              className="date-picker"
            />
            {onToggleFullscreen && (
              <button
                type="button"
                className="btn_fullscreen"
                onClick={onToggleFullscreen}
              >
                {isFullscreen ? <Icons.FullscreenExit /> : <Icons.Fullscreen />}
                {isFullscreen ? "ออกจากเต็มจอ" : "ดูเต็มจอ"}
              </button>
            )}
          </div>
        </div>
        <div className="status_legend">
          {LEGEND_ITEMS.map((e) => (
            <div key={e.color} className="status">
              <span
                className="status-indicator"
                style={{ backgroundColor: e.color }}
              ></span>
              <span className="status-text">{e.labels.join(" / ")}</span>
            </div>
          ))}
          <div className="status">
            <span className="status-indicator gray"></span>
            <span className="status-text">วันหยุด</span>
          </div>
        </div>
        <div className="table_container">
          <div className="time_column">
            <div className="time_column_label">เซ็ทเป็นไม่ว่าง</div>
            {TIME_DATA.map((e, i) => (
              <div key={i} className="time_row">
                {!isHoliday && filterValue === "day"
                  ? `${e.value.startTime} - ${e.value.endTime}`
                  : // <Controller
                    //   control={control}
                    //   render={({ field }) => (
                    //     <Inputs.InputCheckBox
                    //       {...field}
                    //       checked={field.value}
                    //       label={`${e.value.startTime} - ${e.value.endTime}`}
                    //       onChange={(e) => field.onChange({ ...e, isEdit: true })}
                    //     />
                    //   )}
                    //   name={`${e.value.startTime.replace(
                    //     ".",
                    //     "_"
                    //   )}-${e.value.endTime.replace(".", "_")}`}
                    //   defaultValue=""
                    // />
                    `${e.value.startTime} - ${e.value.endTime}`}
              </div>
            ))}
          </div>
          <div className="scheduler_table">
            <div className="main_header">
              {filterValue === "day"
                ? columns.map((col, index) => (
                    <div className="table_header">
                      <div key={index} className="header_item">
                        {col.name_th}
                      </div>
                    </div>
                  ))
                : _renderDay()}
            </div>
            <div className="table_body">
              {filterValue === "week" ? (
                <>
                  <div className="row">{_renderCalendar()}</div>
                  {_renderWeekBookingsOverlay()}
                </>
              ) : (
                <>
                  {TIME_DATA.map((e, i) => (
                    <div key={i} className="row">
                      {_renderCutomer(e)}
                    </div>
                  ))}
                  {_renderBookingsOverlay()}
                </>
              )}
            </div>
          </div>
        </div>
      </form>
    </SchedulerStyled>
  );
};

const TIME_DATA = [
  { label: "07.00\n07.30", value: { startTime: "07.00", endTime: "07.30" } },
  { label: "07.30\n08.00", value: { startTime: "07.30", endTime: "08.00" } },
  { label: "08.00\n08.30", value: { startTime: "08.00", endTime: "08.30" } },
  { label: "08.30\n09.00", value: { startTime: "08.30", endTime: "09.00" } },
  { label: "09.00\n09.30", value: { startTime: "09.00", endTime: "09.30" } },
  { label: "09.30\n10.00", value: { startTime: "09.30", endTime: "10.00" } },
  { label: "10.00\n10.30", value: { startTime: "10.00", endTime: "10.30" } },
  { label: "10.30\n11.00", value: { startTime: "10.30", endTime: "11.00" } },
  { label: "11.30\n12.00", value: { startTime: "11.30", endTime: "12.00" } },
  { label: "12.00\n12.30", value: { startTime: "12.00", endTime: "12.30" } },
  { label: "12.30\n13.00", value: { startTime: "12.30", endTime: "13.00" } },
  { label: "13.00\n13.30", value: { startTime: "13.00", endTime: "13.30" } },
  { label: "13.30\n14.00", value: { startTime: "13.30", endTime: "14.00" } },
  { label: "14.00\n14.30", value: { startTime: "14.00", endTime: "14.30" } },
  { label: "14.30\n15.00", value: { startTime: "14.30", endTime: "15.00" } },
  { label: "15.00\n15.30", value: { startTime: "15.00", endTime: "15.30" } },
  { label: "15.30\n16.00", value: { startTime: "15.30", endTime: "16.00" } },
  { label: "16.00\n16.30", value: { startTime: "16.00", endTime: "16.30" } },
  { label: "16.30\n17.00", value: { startTime: "16.30", endTime: "17.00" } },
  { label: "17.00\n17.30", value: { startTime: "17.00", endTime: "17.30" } },
  { label: "17.30\n18.00", value: { startTime: "17.30", endTime: "18.00" } },
  { label: "18.00\n18.30", value: { startTime: "18.00", endTime: "18.30" } },
  { label: "18.30\n19.00", value: { startTime: "18.30", endTime: "19.00" } },
  { label: "19.00\n19.30", value: { startTime: "19.00", endTime: "19.30" } },
  { label: "19.30\n20.00", value: { startTime: "19.30", endTime: "20.00" } },
  { label: "20.00\n20.30", value: { startTime: "20.00", endTime: "20.30" } },
  { label: "20.30\n21.00", value: { startTime: "20.30", endTime: "21.00" } },
  { label: "21.00\n21.30", value: { startTime: "21.00", endTime: "21.30" } },
  { label: "21.30\n22.00", value: { startTime: "21.30", endTime: "22.00" } },
];

export default Scheduler;
